import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { signIn } from '../lib/auth-client';
import { Input } from '@/components/ui/input';
import { BrandLogo } from '@/components/BrandLogo';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle } from 'lucide-react';

const schema = z.object({
  email: z.email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

type FormData = z.infer<typeof schema>;

export default function LoginPage() {
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { email: 'agent@example.com', password: 'password123' },
  });

  async function onSubmit(data: FormData) {
    const { error: authError } = await signIn.email(data);
    if (authError) {
      setError('root', {
        type: 'manual',
        message: authError.message ?? 'Invalid email or password',
      });
    } else {
      window.location.href = '/';
    }
  }

  return (
    <div
      className='relative min-h-screen flex items-center justify-center dot-grid overflow-hidden'
      style={{ background: 'var(--background)' }}
    >
      {/* Subtle violet glow behind the card */}
      <div
        className='absolute pointer-events-none'
        style={{
          width: 'min(600px, 150vw)',
          height: 'min(600px, 150vw)',
          background: 'radial-gradient(circle, oklch(0.65 0.22 285 / 0.07) 0%, transparent 70%)',
          transform: 'translate(-50%, -50%)',
          left: '50%',
          top: '50%',
        }}
      />

      <div
        className='relative w-full max-w-[400px] mx-4 rounded-xl overflow-hidden'
        style={{
          background: 'var(--card)',
          border: '1px solid oklch(0.65 0.22 285 / 0.2)',
          boxShadow: '0 0 0 1px oklch(0.65 0.22 285 / 0.1), 0 32px 64px oklch(0 0 0 / 0.5)',
        }}
      >
        {/* Top accent bar */}
        <div
          className='h-px w-full'
          style={{ background: 'linear-gradient(90deg, transparent, oklch(0.65 0.22 285 / 0.6), transparent)' }}
        />

        <div className='px-5 pt-7 pb-2 sm:px-8 sm:pt-8'>
          <div className='mb-6 sm:mb-8'>
            <BrandLogo />
          </div>

          <h1 className='text-xl font-bold font-syne mb-1'>
            Sign in
          </h1>
          <p className='text-sm mb-5 sm:mb-7 text-muted-foreground'>
            Enter your credentials to access the dashboard
          </p>
        </div>

        <div className='px-5 pb-6 sm:px-8 sm:pb-8'>
          <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-5'>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='email' className='text-xs font-bold tracking-wide text-muted-foreground'>
                Email
              </Label>
              <Input
                id='email'
                type='email'
                autoFocus
                className='py-5 h-10'
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p className='text-destructive text-xs'>{errors.email.message}</p>
              )}
            </div>
            <div className='flex flex-col gap-1.5'>
              <Label htmlFor='password' className='text-xs font-bold tracking-wide text-muted-foreground'>
                Password
              </Label>
              <Input
                id='password'
                type='password'
                className='py-5 h-10'
                aria-invalid={!!errors.password}
                {...register('password')}
              />
              {errors.password && (
                <p className='text-destructive text-xs'>{errors.password.message}</p>
              )}
            </div>
            {errors.root && (
              <Alert variant='destructive'>
                <AlertCircle className='h-4 w-4' />
                <AlertDescription>{errors.root.message}</AlertDescription>
              </Alert>
            )}
            <Button
              type='submit'
              disabled={isSubmitting}
              className='w-full mt-1 h-10 cursor-pointer font-syne font-bold tracking-wide'
            >
              {isSubmitting ? 'Signing in…' : 'Sign in'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
